import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaSearch, FaArrowRight, FaShieldAlt, FaBolt, FaUserCheck } from 'react-icons/fa';
import { useLanguage } from '../../contexts/LanguageContext';

const DEFAULT_HERO_IMAGE = 'https://images.unsplash.com/photo-1542751371-adc38448a05e?w=1920&q=80';

const HeroSection = React.memo(({ heroSearch, setHeroSearch, heroImageUrl, heroBrightness = 30 }) => {
    const navigate = useNavigate();
    const { t } = useLanguage();
    const bgImage = heroImageUrl || DEFAULT_HERO_IMAGE;
    const b = heroBrightness / 100; // 0..1
    const [imageLoaded, setImageLoaded] = useState(false);

    // Preload hero image
    useEffect(() => {
        setImageLoaded(false);
        const img = new Image();
        img.onload = () => setImageLoaded(true);
        img.onerror = () => setImageLoaded(true);
        img.src = bgImage;

        const link = document.createElement('link');
        link.rel = 'preload';
        link.as = 'image';
        link.href = bgImage;
        document.head.appendChild(link);

        return () => {
            document.head.removeChild(link);
        };
    }, [bgImage]);

    return (
        <section className="relative overflow-hidden" style={{ background: 'linear-gradient(135deg, #0F172A 0%, #1E293B 50%, #0F172A 100%)' }}>
            {/* Background image */}
            <div className="absolute inset-0" style={{
                backgroundImage: `url("${bgImage}")`,
                backgroundSize: 'cover', backgroundPosition: 'center',
                opacity: imageLoaded ? Math.max(0.15, b) : 0,
                transition: 'opacity 0.6s ease-in-out',
            }} />

            {/* Multi-layer gradient overlays for text readability */}
            {/* Layer 1: Strong center-focused radial gradient */}
            <div className="absolute inset-0" style={{
                background: `radial-gradient(ellipse at center, rgba(15,23,42,0.85) 0%, rgba(15,23,42,0.5) 50%, rgba(15,23,42,0.7) 100%)`,
            }} />
            {/* Layer 2: Top-down gradient for upper text area */}
            <div className="absolute inset-0" style={{
                background: `linear-gradient(to bottom, rgba(15,23,42,0.9) 0%, rgba(15,23,42,0.6) 40%, rgba(15,23,42,0.4) 60%, rgba(15,23,42,0.85) 100%)`,
            }} />
            {/* Layer 3: Subtle vignette for edge darkening */}
            <div className="absolute inset-0" style={{
                background: `radial-gradient(ellipse at center, transparent 40%, rgba(0,0,0,0.3) 100%)`,
            }} />

            <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 pt-28 pb-20 md:pt-36 md:pb-28">
                <div className="text-center">
                    {/* Badge */}
                    <div className="inline-flex items-center gap-2 px-5 py-2 rounded-full mb-6"
                        style={{
                            background: 'rgba(59, 130, 246, 0.15)',
                            backdropFilter: 'blur(12px)',
                            WebkitBackdropFilter: 'blur(12px)',
                            border: '1px solid rgba(96, 165, 250, 0.3)',
                            boxShadow: '0 0 20px rgba(59, 130, 246, 0.1)',
                        }}
                    >
                        <span className="text-blue-300 text-sm font-semibold tracking-wide"
                            style={{ textShadow: '0 1px 3px rgba(0,0,0,0.5)' }}
                        >{t('hero.badge')}</span>
                    </div>

                    {/* Title — strong text shadow for readability */}
                    <h1
                        className="font-black text-white mb-4 leading-tight"
                        style={{
                            fontFamily: "'Space Grotesk', sans-serif",
                            textShadow: '0 2px 8px rgba(0,0,0,0.6), 0 4px 24px rgba(0,0,0,0.4), 0 0 40px rgba(0,0,0,0.2)',
                        }}
                    >
                        <span className="text-3xl md:text-4xl lg:text-5xl">{t('hero.titleLine1')}</span><br />
                        <span className="text-3xl md:text-4xl lg:text-5xl" style={{
                            background: 'linear-gradient(135deg, #FBBF24 0%, #F59E0B 30%, #D97706 60%, #FBBF24 100%)',
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent',
                            backgroundClip: 'text',
                            textShadow: 'none',
                            filter: 'drop-shadow(0 2px 6px rgba(251,191,36,0.4))',
                            textDecoration: 'underline',
                            textDecorationColor: '#FBBF24',
                            textUnderlineOffset: '8px',
                            textDecorationThickness: '3px',
                        }}>{t('hero.titleLine2')}</span>{' '}
                        <span
                            className="text-4xl md:text-5xl lg:text-6xl"
                            style={{
                                fontStyle: 'italic',
                                fontWeight: 900,
                                background: 'linear-gradient(135deg, #60A5FA 0%, #818CF8 40%, #C084FC 100%)',
                                WebkitBackgroundClip: 'text',
                                WebkitTextFillColor: 'transparent',
                                backgroundClip: 'text',
                                textShadow: 'none',
                                filter: 'drop-shadow(0 2px 8px rgba(99,102,241,0.5)) drop-shadow(0 4px 16px rgba(99,102,241,0.3))',
                                letterSpacing: '0.02em',
                            }}
                        >{t('hero.titleBrand')}</span>
                    </h1>

                    <p className="text-lg max-w-2xl mx-auto mb-10 leading-relaxed"
                        style={{
                            color: 'rgba(226, 232, 240, 0.95)',
                            textShadow: '0 1px 6px rgba(0,0,0,0.6), 0 2px 12px rgba(0,0,0,0.3)',
                        }}
                    >
                        {t('hero.subtitleLine1')}<br className="hidden md:block" />
                        {t('hero.subtitleLine2')}
                    </p>

                    {/* Search Bar — glass effect */}
                    <div className="flex flex-col sm:flex-row items-center justify-center gap-3 max-w-xl mx-auto mb-8">
                        <div className="relative w-full">
                            <FaSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                            <input
                                type="text"
                                placeholder={t('hero.searchPlaceholder')}
                                className="w-full pl-12 pr-4 py-4 rounded-xl text-gray-800 text-sm border-0 focus:outline-none focus:ring-2 focus:ring-blue-400"
                                style={{
                                    background: 'rgba(255,255,255,0.95)',
                                    boxShadow: '0 4px 24px rgba(0,0,0,0.2), 0 0 0 1px rgba(255,255,255,0.1)',
                                }}
                                value={heroSearch}
                                onChange={(e) => setHeroSearch(e.target.value)}
                                onKeyDown={(e) => { if (e.key === 'Enter') navigate(heroSearch ? `/games?search=${encodeURIComponent(heroSearch)}` : '/games'); }}
                            />
                        </div>
                        <button
                            onClick={() => navigate(heroSearch ? `/games?search=${encodeURIComponent(heroSearch)}` : '/games')}
                            className="w-full sm:w-auto px-8 py-4 rounded-xl font-bold text-white text-sm flex items-center justify-center gap-2 whitespace-nowrap transition-all hover:-translate-y-0.5 hover:shadow-xl"
                            style={{
                                background: 'linear-gradient(135deg, #3B82F6 0%, #2563EB 100%)',
                                boxShadow: '0 4px 20px rgba(59, 130, 246, 0.4), 0 0 0 1px rgba(59, 130, 246, 0.2)',
                            }}
                        >
                            {t('hero.searchButton')} <FaArrowRight className="text-xs" />
                        </button>
                    </div>

                    {/* Trust Badges — glassmorphism pills */}
                    <div className="flex flex-wrap items-center justify-center gap-4 md:gap-6">
                        {[
                            { icon: FaShieldAlt, text: t('hero.trustBadge1'), color: '#34D399' },
                            { icon: FaBolt, text: t('hero.trustBadge2'), color: '#FBBF24' },
                            { icon: FaUserCheck, text: t('hero.trustBadge3'), color: '#60A5FA' },
                        ].map((badge, i) => (
                            <div
                                key={i}
                                className="flex items-center gap-2.5 px-4 py-2 rounded-full text-sm font-medium"
                                style={{
                                    background: 'rgba(255,255,255,0.08)',
                                    backdropFilter: 'blur(12px)',
                                    WebkitBackdropFilter: 'blur(12px)',
                                    border: '1px solid rgba(255,255,255,0.12)',
                                    boxShadow: '0 2px 12px rgba(0,0,0,0.15)',
                                }}
                            >
                                <badge.icon style={{ color: badge.color, filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.3))' }} />
                                <span className="text-white" style={{ textShadow: '0 1px 4px rgba(0,0,0,0.5)' }}>{badge.text}</span>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </section>
    );
});

HeroSection.displayName = 'HeroSection';

export default HeroSection;
